import type { EditorTPAPreviewHandlers } from './handlers/editorTPAPreviewHandlers';
import type { EditorTPAHandlers } from './handlers/editorTPAHandlers/editorTPAHandlers';
import type { EditorSuperAppsHandlers } from './handlers/editorSuperAppsHandlers';
import type { EditorPublicDataHandlers } from './handlers/editorPublicDataHandlers/editorPublicDataHandlers';
import type { EditorUILibHandlers } from './handlers/editorUILibHandlers/editorUILibHandlers';
import type { EditorEventHandlers } from './handlers';

export const HANDLER_NAMESPACES: Record<
  string,
  Set<keyof EditorEventHandlers>
> = {
  TPA_PREVIEW: new Set<keyof EditorTPAPreviewHandlers>([
    'deselectComponent',
    'onReady',
    'getViewMode',
    'openHelp',
    'openSettingsDialog',
    'editorAddApplication',
    'isCustomApplicationPermissionsGranted',
    'isGroupApplicationPermissionsGranted',
    'editorIsApplicationInstalled',
    'editorIsFullWidth',
  ]),
  TPA: new Set<keyof EditorTPAHandlers>([
    'getWindowPlacement',
    'setWindowPlacement',
    'isComponentInstalled',
    'isApplicationInstalled',
    'appEngaged',
    'addComponent',
    'setFullWidth',
    'isFullWidth',
    'openSiteMembersSettingsDialog',
    'getCurrentPageId',
    'openMediaDialog',
    'openLinkPanel',
    'settingsOpenModal',
    'getDashboardAppUrl',
    'postMessage',
    'refreshLivePreview',
    'refreshAppsOnDashboardClose',
    'migrate',
    'stylesReady',
    'getWixUpgradeUrl',
    'appIsAlive',
    'closeWindow',
    'getProducts',
    'openBillingPage',
    'openReviewInfo',
    'trackAppUpgrade',
    'setStyleParam',
    'setGlobalStyleParam',
    'setExternalId',
    'getExternalId',
    'getPermissions',
  ]),
  SUPER_APPS: new Set<keyof EditorSuperAppsHandlers>([
    'setHelpArticle',
    'openDashboard',
    'superAppsOpenMediaDialog',
    'getSelectedComponentApplicationId',
    'getSelectedComponentAppDefinitionId',
  ]),
  PUBLIC_DATA: new Set<keyof EditorPublicDataHandlers>([
    'setValue',
    'getValue',
    'removeValue',
  ]),
  UI_LIB: new Set<keyof EditorUILibHandlers>([
    'openColorPicker',
    'openFontPicker',
    'openBackgroundPicker',
  ]),
} as const;

export const INTENT_ACCESS_RULES: Record<
  string,
  {
    allowedNamespaces: Set<keyof EditorEventHandlers>;
  }
> = {
  TPA_PREVIEW: {
    allowedNamespaces: HANDLER_NAMESPACES.TPA_PREVIEW,
  },
  TPA: {
    allowedNamespaces: new Set([
      ...HANDLER_NAMESPACES.TPA,
      ...HANDLER_NAMESPACES.SUPER_APPS,
      ...HANDLER_NAMESPACES.PUBLIC_DATA,
      ...HANDLER_NAMESPACES.UI_LIB,
      ...HANDLER_NAMESPACES.TPA_PREVIEW,
    ]),
  },
  TPA2: {
    allowedNamespaces: new Set([
      ...HANDLER_NAMESPACES.TPA,
      ...HANDLER_NAMESPACES.SUPER_APPS,
      ...HANDLER_NAMESPACES.PUBLIC_DATA,
      ...HANDLER_NAMESPACES.UI_LIB,
      ...HANDLER_NAMESPACES.TPA_PREVIEW,
    ]),
  },
};

export interface HandlerAccessValidation {
  isAllowed: boolean;
  error?: string;
}

const isHandlerInAnyNamespace = (
  handlerType: keyof EditorEventHandlers,
): boolean => {
  return Object.values(HANDLER_NAMESPACES).some((namespace) =>
    namespace.has(handlerType),
  );
};

export const validateHandlerAccess = (
  intent: string,
  handlerType: keyof EditorEventHandlers,
): HandlerAccessValidation => {
  const accessRule = INTENT_ACCESS_RULES[intent];

  // restrict access to unknown intent
  if (!accessRule) {
    return { isAllowed: false };
  }

  const isKnownHandler = isHandlerInAnyNamespace(handlerType);

  // allow access to unknown handler for backward compatibility
  if (!isKnownHandler) {
    return { isAllowed: true };
  }

  const hasAccess = accessRule.allowedNamespaces.has(handlerType);

  if (!hasAccess) {
    return {
      isAllowed: false,
      error: `Access denied: Intent "${intent}" attempted to call handler "${handlerType}" which is not in the allowed namespace. Allowed handlers for this intent: ${Array.from(
        accessRule.allowedNamespaces,
      ).join(', ')}`,
    };
  }

  return { isAllowed: true };
};
